/**
 * WebSocketClient — standalone WebSocket transport with JSON-RPC
 * and event subscription support.
 *
 * Pure TypeScript, no React dependency. Owns:
 *   - socket lifecycle (connect / reconnect / backoff / heartbeat)
 *   - JSON-RPC request/response tracking (rpc / rpcStream)
 *   - server-push event dispatch (onSchemaEvent / onEventType)
 *   - connection status with external listeners (subscribeStatus)
 */

import {
  HEARTBEAT_MS,
  MAX_BACKOFF_MS,
  RPC_STREAM_TIMEOUT_MS,
  RPC_TIMEOUT_MS,
} from "./constants.ts";
import {
  RpcError,
  type RpcErrorData,
} from "../contracts/transport.ts";

// ─── Public types ────────────────────────────────────────────────────

export interface Rpc {
  rpc<T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ): Promise<T>;
  rpcStream(
    method: string,
    params: Record<string, unknown>,
    onChunk: (data: unknown) => void,
  ): Promise<unknown>;
  readonly baseUrl: string;
}

export interface StreamEvent {
  readonly type: string;
  readonly payload?: unknown;
  readonly timestamp: string;
}

export interface ConnectionStatus {
  readonly connected: boolean;
  readonly label: string;
}

// ─── Internal types ──────────────────────────────────────────────────

interface PendingRequest {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
  onChunk?: (d: unknown) => void;
  timer: ReturnType<typeof setTimeout>;
}

type MessageHandler = (data: unknown) => void;
type VoidHandler = () => void;
type StatusListener = (status: ConnectionStatus) => void;

function parseRpcErrorData(value: unknown): RpcErrorData | null {
  if (value === null || typeof value !== "object") return null;
  const data = value as Record<string, unknown>;
  if (
    typeof data.kind !== "string" ||
    typeof data.retryable !== "boolean" ||
    typeof data.display_message !== "string"
  ) {
    return null;
  }
  return {
    kind: data.kind,
    retryable: data.retryable,
    display_message: data.display_message,
  };
}

// Per-method RPC timeout overrides. Most calls cap at RPC_TIMEOUT_MS (30s), but
// `tool_approvals.execute` drives a tool to completion (e.g. a Telegram send
// that may briefly wait on a FloodWait) — capping it at 30s surfaces a raw
// "RPC timeout" while the tool is still running server-side. There is no
// backend cancel for it, so the only honest fix is a longer client timeout.
// [DEC-3 / INV-4]
const RPC_METHOD_TIMEOUT_MS: Readonly<Record<string, number>> = {
  "tool_approvals.execute": RPC_STREAM_TIMEOUT_MS,
};

// ─── Human-readable status labels ────────────────────────────────────
// Plain, non-scary, actionable. Raw WebSocket close codes (1000, 4401, …)
// are NEVER shown to the user — they mean nothing to them and read as a
// riddle. We translate (code, reason) into one of these.
const STATUS = {
  connecting: "Connecting…",
  signingIn: "Signing in…",
  connected: "Connected",
  disconnected: "Disconnected",
  notSignedIn: "Not signed in — please log in",
  authFailed: "Sign-in failed — please log in again",
  refused: "Server refused the connection — check it's running and reachable",
  unreachable: "Can't reach the server — reconnecting…",
  lost: "Lost connection to the server — reconnecting…",
} as const;

/** True when a close (code + reason) is the backend rejecting our auth —
 *  4401 "unauthorized", 4403, 1008, or an unauthorized/token reason. */
function isAuthRejectClose(code: number | undefined, reason: string, errReason: string | null): boolean {
  const detail = `${reason} ${errReason ?? ""}`.toLowerCase();
  return (
    code === 4401 ||
    code === 4403 ||
    code === 1008 ||
    /unauthor|forbidden|token|credential/.test(detail)
  );
}

/** Translate a WebSocket close (code + reason) into a plain-language line —
 *  never a bare numeric code. The backend closes auth failures with 4401
 *  "unauthorized"; a normal closure (1000) is the server going away, NOT a
 *  login problem. */
function humanizeDisconnect(
  code: number | undefined,
  reason: string,
  errReason: string | null,
): string {
  const detail = `${reason} ${errReason ?? ""}`.toLowerCase();
  // Auth rejected — actionable: the user must sign in again.
  if (isAuthRejectClose(code, reason, errReason)) {
    return STATUS.authFailed;
  }
  // Upgrade refused (e.g. CORS/Origin not allowed → "Expected 101 status code").
  if (code === 403 || /expected 101|status code|origin|cors/.test(detail)) {
    return STATUS.refused;
  }
  // Never reached the server (refused / dropped).
  if (code === undefined || code === 1006) {
    return STATUS.unreachable;
  }
  // Clean / other close (1000, 1001, 1011, …): the server went away. Retrying.
  return STATUS.lost;
}

// ─── Client ──────────────────────────────────────────────────────────

export class WebSocketClient {
  readonly baseUrl: string;

  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private backoff = 1000;
  private intentionalClose = false;
  /** True between scheduling a reconnect and the next successful auth. While
   *  set, `onopen` keeps the steady "disconnected … reconnecting…" label
   *  instead of re-flashing the transient "Authenticating…" each cycle. */
  private reconnecting = false;
  /** Set when THIS connection attempt got no auth token (bootstrap returned
   *  null while the socket was open) — so the close reads as "Not signed in"
   *  rather than a generic dropped-connection. Reset each attempt. */
  private noTokenThisAttempt = false;
  private counter = 0;
  private authed = false;
  /** Set when THIS attempt sent a NON-empty token. If the server then rejects
   *  it (auth-fail close), the token is stale — we clear it via `onAuthReject`
   *  so the next attempt goes token-less (Open-mode backends admit that as the
   *  default user, instead of wedging forever re-sending a dead token). */
  private tokenPresentedThisAttempt = false;
  /** Reason captured from the last upgrade error / close, surfaced in the
   *  status label so the UI can show *why* a connection dropped. */
  private lastErrorReason: string | null = null;
  /**
   * Supplier for the current auth token. Called on each WS open
   * to fetch a fresh JWT so the token can be rotated without the
   * client caching stale values. Returning `null` aborts the
   * connection attempt (review item B3 / D1).
   */
  private readonly getAuthToken:
    | (() => string | null | Promise<string | null>)
    | null;
  /** Called when a presented token is REJECTED by the server (stale/invalid).
   *  The integration clears the stored token so the retry reconnects
   *  token-less. Without this a bad token wedges the app in "Connecting…". */
  private readonly onAuthReject: (() => void) | null;

  private readonly pending = new Map<string, PendingRequest>();
  private readonly messageHandlers = new Set<MessageHandler>();
  private readonly connectHandlers = new Set<VoidHandler>();
  private readonly disconnectHandlers = new Set<VoidHandler>();
  private readonly statusListeners = new Set<StatusListener>();

  private currentStatus: ConnectionStatus = {
    connected: false,
    label: "Initializing...",
  };

  constructor(
    baseUrl: string,
    getAuthToken?: () => string | null | Promise<string | null>,
    onAuthReject?: () => void,
  ) {
    this.baseUrl = baseUrl;
    this.getAuthToken = getAuthToken ?? null;
    this.onAuthReject = onAuthReject ?? null;
  }

  // ── Lifecycle ───────────────────────────────────────────────────

  connect(): void {
    this.intentionalClose = false;
    this.reconnecting = false;
    this.createConnection();
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.cleanup();
    this.setStatus(false, "Disconnected");
  }

  // ── RPC ─────────────────────────────────────────────────────────

  async rpc<T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ): Promise<T> {
    await this.waitForConnection();
    const timeoutMs = RPC_METHOD_TIMEOUT_MS[method] ?? RPC_TIMEOUT_MS;
    return new Promise<T>((resolve, reject) => {
      const id = `req_${++this.counter}`;
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`RPC timeout: ${method} (${timeoutMs}ms)`));
      }, timeoutMs);
      this.pending.set(id, {
        resolve: resolve as (v: unknown) => void,
        reject,
        timer,
      });
      this.ws!.send(JSON.stringify({ id, method, params: params ?? {} }));
    });
  }

  async rpcStream(
    method: string,
    params: Record<string, unknown>,
    onChunk: (d: unknown) => void,
  ): Promise<unknown> {
    await this.waitForConnection();
    return new Promise<unknown>((resolve, reject) => {
      const id = `req_${++this.counter}`;
      const onTimeout = (): void => {
        this.pending.delete(id);
        reject(new Error(`RPC stream timeout: ${method}`));
      };
      // INACTIVITY timeout, reset on every frame: a live stream (steady
      // deltas/tool events) must never be killed mid-turn — long agent
      // turns legitimately exceed any flat cap. Silence for the full
      // window means the connection is dead, which is what this detects.
      this.pending.set(id, {
        resolve: resolve as (v: unknown) => void,
        reject,
        onChunk: (chunk: unknown) => {
          const entry = this.pending.get(id);
          if (entry) {
            clearTimeout(entry.timer);
            entry.timer = setTimeout(onTimeout, RPC_STREAM_TIMEOUT_MS);
          }
          onChunk(chunk);
        },
        timer: setTimeout(onTimeout, RPC_STREAM_TIMEOUT_MS),
      });
      this.ws!.send(JSON.stringify({ id, method, params }));
    });
  }

  // ── Event subscriptions ─────────────────────────────────────────

  onSchemaEvent(
    schemaIds: readonly string[],
    handler: (e: StreamEvent) => void,
  ): () => void {
    const allowed = new Set(schemaIds);
    return this.onMessage((data) => {
      const ev = this.parseEvent(data);
      if (!ev) return;
      const p = ev.payload as { schema_id?: string } | undefined;
      if (typeof p?.schema_id === "string" && allowed.has(p.schema_id))
        handler(ev);
    });
  }

  onEventType(
    types: readonly string[],
    handler: (e: StreamEvent) => void,
  ): () => void {
    const allowed = new Set(types);
    return this.onMessage((data) => {
      const ev = this.parseEvent(data);
      if (ev && allowed.has(ev.type)) handler(ev);
    });
  }

  // ── Status observation ──────────────────────────────────────────

  getStatus(): ConnectionStatus {
    return this.currentStatus;
  }

  subscribeStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  // ── Connection listeners ────────────────────────────────────────

  onConnect(h: VoidHandler): () => void {
    this.connectHandlers.add(h);
    return () => {
      this.connectHandlers.delete(h);
    };
  }

  onDisconnect(h: VoidHandler): () => void {
    this.disconnectHandlers.add(h);
    return () => {
      this.disconnectHandlers.delete(h);
    };
  }

  // ── Internals ───────────────────────────────────────────────────

  private onMessage(h: MessageHandler): () => void {
    this.messageHandlers.add(h);
    return () => {
      this.messageHandlers.delete(h);
    };
  }

  private setStatus(connected: boolean, label: string): void {
    // No-op if nothing changed — avoids redundant listener churn (and UI
    // re-renders) when the same status is set repeatedly during reconnects.
    if (this.currentStatus.connected === connected && this.currentStatus.label === label) return;
    this.currentStatus = { connected, label };
    this.statusListeners.forEach((l) => { l(this.currentStatus); });
  }

  private send(msg: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN)
      this.ws.send(JSON.stringify(msg));
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer != null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(
      () => { this.send({ type: "ping", ts: Date.now() }); },
      HEARTBEAT_MS,
    );
  }

  private rejectAllPending(reason: string): void {
    for (const [id, p] of this.pending) {
      clearTimeout(p.timer);
      p.reject(new Error(reason));
      this.pending.delete(id);
    }
  }

  private handleMessage(data: unknown): void {
    const msg = data as {
      id?: string;
      result?: unknown;
      error?: { code: number; message: string; data?: unknown };
      stream?: unknown;
      type?: string;
    };

    if (msg.type === "auth_ok") {
      this.authed = true;
      // A fully-established connection — reset backoff and end the reconnect
      // cycle so the NEXT drop starts fresh at 1s and shows "Signing in…".
      this.backoff = 1000;
      this.reconnecting = false;
      this.noTokenThisAttempt = false;
      this.setStatus(true, STATUS.connected);
      this.connectHandlers.forEach((h) => {
        h();
      });
      return;
    }

    if (msg.id && "result" in msg) {
      const p = this.pending.get(msg.id);
      if (p) {
        clearTimeout(p.timer);
        this.pending.delete(msg.id);
        p.resolve(msg.result);
      }
      return;
    }
    if (msg.id && msg.error) {
      const p = this.pending.get(msg.id);
      if (p) {
        clearTimeout(p.timer);
        this.pending.delete(msg.id);
        p.reject(
          new RpcError(
            msg.error.code,
            msg.error.message,
            parseRpcErrorData(msg.error.data),
          ),
        );
      }
      return;
    }
    if (msg.id && "stream" in msg) {
      const p = this.pending.get(msg.id);
      if (p?.onChunk) p.onChunk(msg.stream);
      return;
    }
    this.messageHandlers.forEach((h) => { h(data); });
  }

  private cleanup(): void {
    this.stopHeartbeat();
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close();
      }
      this.ws = null;
    }
  }

  /**
   * Origin to present on the `/ws` upgrade in a non-browser runtime.
   * `MAGNIS_CLI_ORIGIN` overrides; otherwise the origin is derived from
   * `baseUrl`. Browsers never call this — they set Origin themselves.
   * `process` is read defensively so the shared browser bundle (no node
   * types) still type-checks.
   */
  private cliOrigin(): string {
    const env = (
      globalThis as { process?: { env?: Record<string, string | undefined> } }
    ).process?.env?.MAGNIS_CLI_ORIGIN;
    if (env) return env;
    try {
      return new URL(this.baseUrl).origin;
    } catch {
      return this.baseUrl;
    }
  }

  private createConnection(): void {
    this.cleanup();
    this.authed = false;
    this.lastErrorReason = null;
    this.noTokenThisAttempt = false;
    this.tokenPresentedThisAttempt = false;
    const wsUrl = this.baseUrl.replace(/^http/, "ws") + "/ws";
    // The CLI runs the WHATWG `WebSocket` on Bun/Node, which sends NO
    // `Origin` header — but the backend gates the `/ws` upgrade on
    // `CORS_ALLOWED_ORIGINS`, so a non-browser client MUST present an
    // allowed Origin or the upgrade 403s. Browsers set Origin themselves
    // (and reject a manual one), so the options bag is passed off-DOM only.
    // The WHATWG type omits the options bag Bun/Node accept — construct
    // through a permissive signature.
    const WS = WebSocket as unknown as new (u: string, o?: unknown) => WebSocket;
    const isBrowser =
      typeof (globalThis as { window?: unknown }).window !== "undefined";
    this.ws = isBrowser
      ? new WS(wsUrl)
      : new WS(wsUrl, { headers: { Origin: this.cliOrigin() } });

    this.ws.onopen = () => {
      // NB: backoff is NOT reset here. A socket that opens then immediately
      // closes (e.g. server closes with 1000 right after the upgrade) would
      // otherwise reset backoff every attempt → a ~1Hz reconnect hammer.
      // Backoff resets only on a real connection (auth_ok / no-auth open).
      this.startHeartbeat();
      // Send auth frame as the first message if a provider is
      // configured. Connection status is not flipped to
      // "Connected" until the server replies with auth_ok —
      // keeping RPC callers blocked in waitForConnection until
      // auth completes.
      if (this.getAuthToken !== null) {
        // Only flash "Signing in…" on a FRESH connect; during a reconnect keep
        // the steady disconnect label (no flicker).
        if (!this.reconnecting) this.setStatus(false, STATUS.signingIn);
        void this.performAuthHandshake();
      } else {
        this.authed = true;
        this.backoff = 1000;
        this.reconnecting = false;
        this.setStatus(true, STATUS.connected);
        this.connectHandlers.forEach((h) => {
          h();
        });
      }
    };
    this.ws.onmessage = (e: MessageEvent) => {
      try {
        this.handleMessage(JSON.parse(e.data as string));
      } catch {
        /* non-JSON */
      }
    };
    this.ws.onclose = (ev?: { code?: number; reason?: string }) => {
      this.stopHeartbeat();
      this.authed = false;
      this.rejectAllPending("WebSocket disconnected");
      // Stale-token self-heal: if we PRESENTED a token and the server rejected
      // it (auth-fail close), the token is dead. Clear it so the immediate
      // reconnect goes token-less — an Open-mode backend then admits the local
      // default user instead of the app wedging forever re-sending a dead
      // token. (Required-mode still closes token-less → "Not signed in".)
      if (
        !this.intentionalClose &&
        this.tokenPresentedThisAttempt &&
        this.onAuthReject !== null &&
        isAuthRejectClose(ev?.code, ev?.reason ?? "", this.lastErrorReason)
      ) {
        this.tokenPresentedThisAttempt = false;
        this.onAuthReject();
      }
      // Show WHY in plain language — never a raw close code (those read as a
      // scary riddle). No token while open → "Not signed in"; otherwise map
      // the close code/reason (auth / refused / unreachable / lost).
      this.setStatus(
        false,
        this.intentionalClose
          ? STATUS.disconnected
          : this.noTokenThisAttempt
            ? STATUS.notSignedIn
            : humanizeDisconnect(ev?.code, ev?.reason ?? "", this.lastErrorReason),
      );
      this.disconnectHandlers.forEach((h) => { h(); });
      if (!this.intentionalClose) {
        this.reconnecting = true;
        this.reconnectTimer = setTimeout(
          () => { this.createConnection(); },
          this.backoff,
        );
        this.backoff = Math.min(this.backoff * 2, MAX_BACKOFF_MS);
      }
    };
    // The failed-upgrade reason (e.g. "Expected 101 status code" for a 403
    // origin reject) arrives on `error` before `close`; capture it so the
    // close handler can surface it.
    this.ws.onerror = (ev: Event) => {
      const m = (ev as unknown as { message?: string }).message;
      if (typeof m === "string" && m.length > 0) this.lastErrorReason = m;
    };
  }

  private async performAuthHandshake(): Promise<void> {
    if (this.getAuthToken === null) return;
    let token: string | null;
    try {
      token = await this.getAuthToken();
    } catch {
      token = null;
    }
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    if (token === null || token.length === 0) {
      // No stored token — do NOT give up by closing the socket. Send a
      // token-less auth frame and let the SERVER decide: a local single-user
      // (Open-mode) backend admits it as the default user (auth_ok →
      // connected), so the local app ALWAYS connects without a token; a
      // multi-user (Required-mode) backend rejects it (4401 → onclose →
      // "Not signed in"). `noTokenThisAttempt` keeps that close reading as
      // "Not signed in" rather than a generic drop.
      this.noTokenThisAttempt = true;
      this.ws.send(JSON.stringify({ type: "auth" }));
      return;
    }
    this.tokenPresentedThisAttempt = true;
    this.ws.send(JSON.stringify({ type: "auth", token }));
  }

  private waitForConnection(timeoutMs = 10_000): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN && this.authed) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        td();
        reject(new Error("WebSocket connection timeout"));
      }, timeoutMs);
      const td = (): void => {
        clearTimeout(timer);
        off1();
        off2();
      };
      const off1 = this.onConnect(() => {
        td();
        resolve();
      });
      const off2 = this.onDisconnect(() => {
        td();
        reject(new Error("WebSocket disconnected while waiting"));
      });
    });
  }

  private parseEvent(data: unknown): StreamEvent | null {
    const msg = data as {
      type?: string;
      event_type?: string;
      payload?: unknown;
      timestamp?: string;
    };
    if (msg.type !== "event") return null;
    return {
      type: msg.event_type ?? "unknown",
      payload: msg.payload,
      timestamp: msg.timestamp ?? new Date().toISOString(),
    };
  }
}
